import React, { useMemo, useState } from 'react';
import { addDays, eachDayOfInterval, format, isFirstDayOfMonth, isSameDay } from 'date-fns';
import { TZDate } from '@date-fns/tz';

import { Text } from '@zillow/constellation';
import { TourStatusForZcheduler, type TourWebApiDto } from '@zg-rentals/ts-z-calendar-client';
import { useZCalendarWebApiHandler_getToursForListing } from '@zg-rentals/ts-z-calendar-client-react-query';

import { StyledCarousel, StyledDate, StyledMonth } from './StyledComponents';
import BookedToursSection from './components/BookedToursSection';
import { convertDateToLocalDate, formatTimeString } from '../../utils/formatTime';

import { LoadingHeading } from '@zg-rentals/loading-components';

import type { Tour } from '../../constants/UpcomingToursConstants';

type UpcomingToursProps = {
  inquiriesPath: string;
  isScreenMobile: boolean;
  listingAlias: string;
  onCalendarClick: () => void;
  tourCalendarPath: string;
  eventId?: string;
};

const UpcomingTours = ({
  inquiriesPath,
  isScreenMobile,
  listingAlias,
  onCalendarClick,
  tourCalendarPath,
  eventId,
}: UpcomingToursProps) => {
  const [selectedDate, setSelectedDate] = useState(new Date());

  const currentDates = useMemo(() => {
    const end = addDays(selectedDate, 13);
    return eachDayOfInterval({ start: selectedDate, end });
  }, []);

  const formatDateTourKey = (date: Date) => format(date, 'yyyy-MM-dd');

  const formatTourTime = (tour: TourWebApiDto) => {
    const startDate = new TZDate(tour.startMs, tour.zoneId);
    const endDate = new TZDate(tour.endMs, tour.zoneId);

    const startTime = formatTimeString(startDate, { truncateZeroMinutes: true });
    const endTime = formatTimeString(endDate, { showMeridiem: true });

    return `${startTime}-${endTime}`;
  };

  const { data, isLoading } = useZCalendarWebApiHandler_getToursForListing({
    listingAlias,
    bookedByBrand: '',
    bookedByUserId: '',
    fromDate: convertDateToLocalDate(currentDates[0]),
    toDate: convertDateToLocalDate(currentDates[currentDates.length - 1]),
    tourStatuses: [TourStatusForZcheduler.CONFIRMED],
  });

  const toursByDate = useMemo(
    () =>
      data?.tours.reduce<Record<string, Array<Tour>>>((acc, tour) => {
        const date = new Date(tour.startMs);

        const key = formatDateTourKey(date);
        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push({
          conversationId: tour.conversationId,
          tourId: tour.id,
          tourName: tour.bookedByName,
          tourTime: formatTourTime(tour),
        });

        return acc;
      }, {}) ?? {},
    [data],
  );

  const getToursForDate = (date: Date) => toursByDate[formatDateTourKey(date)] ?? [];

  const selectedDateTours = getToursForDate(selectedDate);

  const carouselHeading = (
    <LoadingHeading isLoading={isLoading} loadingWidth="100%" level={3}>
      <Text
        className="ConnectedCard-Heading"
        as="h2"
        fontType={{ default: 'bodyHeading', lg: 'h5' }}
        marginRight={{ default: 'xs', lg: 'sm' }}
      >
        Upcoming tours
      </Text>
    </LoadingHeading>
  );

  const formatDate = (date: Date) => {
    const formatPattern = isScreenMobile ? 'eeeee' : 'eee';
    return format(date, formatPattern).toUpperCase();
  };

  return (
    <>
      <StyledCarousel heading={carouselHeading} slideGap={1.5}>
        {currentDates.map((date: Date, index: number) => (
          <React.Fragment key={index}>
            <StyledMonth fontType="bodySmall" marginBottom="xs">
              {(index === 0 || isFirstDayOfMonth(date)) && format(date, 'LLL')}
            </StyledMonth>
            <StyledDate
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
              isSelected={isSameDay(date, selectedDate)}
              onClick={() => setSelectedDate(date)}
              data-testid={`UpcomingTours-date-${index}`}
            >
              <Text fontType="legal">{formatDate(date)}</Text>
              <Text fontType="bodyHeading">{format(date, 'd')}</Text>
              {getToursForDate(date)?.length > 0 && (
                <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
                  <circle cx="3" cy="3" r="3" fill="#DB3A00" stroke="none" />
                </svg>
              )}
            </StyledDate>
          </React.Fragment>
        ))}
      </StyledCarousel>

      <BookedToursSection
        inquiriesPath={inquiriesPath}
        onCalendarClick={onCalendarClick}
        tours={selectedDateTours}
        tourCalendarPath={tourCalendarPath}
        isLoading={isLoading}
        eventId={eventId}
      />
    </>
  );
};

export default UpcomingTours;
